/**
 * Cliente fetch tipado para el backend.
 *
 * Ante 401 en un endpoint /api/*, redirige al login preservando ?next=<ruta actual>.
 * En dev, /api/* está proxeado por Vite al backend (ver vite.config.ts).
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Opciones = Omit<RequestInit, 'body'> & {
  body?: unknown;
  // Si es true, un 401 NO redirige a login (útil para /api/me en el bootstrap del front).
  silent401?: boolean;
};

export async function api<T = unknown>(path: string, opts: Opciones = {}): Promise<T> {
  const { body, silent401, headers, ...rest } = opts;
  const res = await fetch(path, {
    ...rest,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && !silent401) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/api/auth/login?next=${next}`;
    throw new ApiError(401, 'No autenticado');
  }

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    const msg =
      (payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error: string }).error
        : null) || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, payload);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
